/**
 * sdk.conversation — Streaming vers le ConversationEngine backend.
 * POST /api/v1/chat/stream (SSE)
 */
/**
 * Appelle POST /api/v1/chat/stream et yield les événements SSE parsés.
 * Utilise fetch + ReadableStream (compatible navigateur et Node 18+).
 */
export async function* conversationStream(request, token, baseUrl = '') {
    const tenantId = request.tenant_id ?? request.tenantId;
    const res = await fetch(`${baseUrl}/api/v1/chat/stream`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
        },
        body: JSON.stringify(request),
    });
    if (!res.ok) {
        let detail;
        try {
            detail = await res.text();
        }
        catch {
            detail = `HTTP ${res.status}`;
        }
        yield { type: 'error', detail };
        return;
    }
    if (!res.body) {
        yield { type: 'error', detail: 'No response body' };
        return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
        const { done, value } = await reader.read();
        if (done)
            break;
        buffer += decoder.decode(value, { stream: true });
        // sse_starlette envoie \r\n — normalise en \n pour le parsing
        buffer = buffer.replace(/\r\n/g, '\n');
        // SSE : chaque événement séparé par \n\n
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';
        for (const part of parts) {
            const line = part.trim();
            if (!line || line.startsWith(':'))
                continue; // ping / commentaire
            // Format sse_starlette : "event: TYPE\ndata: JSON"
            const eventMatch = line.match(/^event:\s*(\S+)/m);
            const dataMatch = line.match(/^data:\s*(.+)$/m);
            if (!dataMatch)
                continue;
            const dataStr = dataMatch[1];
            if (!dataStr)
                continue;
            let payload = {};
            try {
                payload = JSON.parse(dataStr);
            }
            catch {
                continue;
            }
            const eventType = eventMatch?.[1] ?? payload['event'] ?? 'unknown';
            switch (eventType) {
                case 'chunk':
                case 'token':
                    yield { type: 'token', content: String(payload['content'] ?? payload['text'] ?? '') };
                    break;
                case 'done':
                    yield { type: 'done', text: String(payload['text'] ?? payload['result'] ?? '') };
                    return;
                case 'command_result':
                case 'command':
                    yield { type: 'command', result: String(payload['result'] ?? payload['text'] ?? '') };
                    break;
                case 'suspended':
                    yield { type: 'suspended', suspension_id: String(payload['suspension_id'] ?? '') };
                    return;
                case 'error':
                    yield { type: 'error', detail: String(payload['detail'] ?? payload['message'] ?? 'Unknown error') };
                    return;
                case 'usage':
                    yield {
                        type: 'usage',
                        model_tier: String(payload['model_tier'] ?? 'local'),
                        tokens_used: Number(payload['tokens_used'] ?? 0),
                        cost_eur: Number(payload['cost_eur'] ?? 0),
                        zero_token: Boolean(payload['zero_token'] ?? false),
                        model_id: payload['model_id'] != null ? String(payload['model_id']) : null,
                    };
                    break;
                case 'status':
                    yield {
                        type: 'status',
                        step: String(payload['step'] ?? ''),
                        text: String(payload['text'] ?? ''),
                        _debug: payload['_debug'] != null
                            ? payload['_debug']
                            : null,
                    };
                    break;
                case 'tool':
                    yield {
                        type: 'tool',
                        tool_name: String(payload['tool_name'] ?? ''),
                        arguments: payload['arguments'] != null
                            ? payload['arguments']
                            : null,
                    };
                    break;
            }
        }
    }
}
//# sourceMappingURL=conversation.js.map